import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { showCaptcha } from '../api/captcha';
import { login } from '../state/auth';
import logo from '../assets/logo.png';

// Admin 登录：双因子 = 账号(手机/邮箱) + 密码 + 验证码（对齐 BFF /v1/auth/admin/login）。
// 发送验证码强制人机验证（先弹腾讯 TencentCaptcha 拿 ticket 再发码，purpose=login）。
// 后端 mw.AdminRequired 校验 env ADMIN_USER_IDS 白名单——非 admin 登录成功但访问
// /v1/admin/* 返回 403（在各功能页捕获）。
export default function LoginPage() {
  const nav = useNavigate();
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = () => {
    setCooldown(60);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1 && timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const sendCode = async () => {
    if (!account || sending || cooldown > 0) return;
    setErr(null);
    // 发码强制人机验证：先弹验证码拿 ticket
    const ticket = await showCaptcha();
    if (!ticket) {
      setErr('请完成人机验证后再获取验证码');
      return;
    }
    setSending(true);
    try {
      await api('/auth/code/send', {
        method: 'POST',
        json: { account, purpose: 'login', region: 'cn', captchaTicket: ticket },
      });
      startCooldown();
    } catch (e) {
      setErr((e as ApiError).message);
    } finally {
      setSending(false);
    }
  };

  const doLogin = async () => {
    if (!account || !password || !code) return;
    setLoggingIn(true);
    setErr(null);
    try {
      const resp = await api<{ token: string; userId: string }>(
        '/auth/admin/login',
        { method: 'POST', json: { account, password, code, region: 'cn' } },
      );
      login(resp.token, resp.userId);
      nav('/', { replace: true });
    } catch (e) {
      setErr((e as ApiError).message);
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <div style={S.wrap}>
      <div style={S.card}>
        <img src={logo} alt="家理理" width={72} height={72} style={S.logo} />
        <h1 style={S.title}>家理理 Admin</h1>
        <p style={S.subtitle}>审核 / 运营 / 内部工具</p>

        <label style={S.label}>账号</label>
        <input
          style={S.input}
          value={account}
          onChange={(e) => setAccount(e.target.value)}
          placeholder="手机号 / 邮箱"
          autoComplete="username"
          autoFocus
        />

        <label style={S.label}>密码</label>
        <input
          style={S.input}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="密码"
          autoComplete="current-password"
        />

        <label style={S.label}>验证码</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            style={{ ...S.input, flex: 1 }}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="6 位数字"
            maxLength={6}
            inputMode="numeric"
            onKeyDown={(e) => {
              if (e.key === 'Enter') doLogin();
            }}
          />
          <button
            style={S.codeBtn}
            disabled={!account || cooldown > 0 || sending}
            onClick={sendCode}
          >
            {cooldown > 0 ? `${cooldown}s` : sending ? '...' : '获取验证码'}
          </button>
        </div>

        {err && <div style={S.err}>{err}</div>}

        <button
          style={S.primaryBtn}
          disabled={!account || !password || !code || loggingIn}
          onClick={doLogin}
        >
          {loggingIn ? '登录中…' : '登录'}
        </button>

        <p style={S.foot}>
          仅 admin 白名单内的账号可访问。普通用户登录后访问审核页会 403。
        </p>
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  wrap: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f5f5f7',
  },
  card: {
    width: 360,
    padding: 32,
    background: 'white',
    borderRadius: 12,
    boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
  },
  logo: { display: 'block', margin: '0 auto 12px', borderRadius: 16 },
  title: { margin: 0, fontSize: 24, fontWeight: 700 },
  subtitle: { marginTop: 4, color: '#888', fontSize: 13 },
  label: { display: 'block', marginTop: 16, fontSize: 13, color: '#444' },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 12px',
    marginTop: 4,
    border: '1px solid #ddd',
    borderRadius: 6,
    fontSize: 14,
  },
  codeBtn: {
    padding: '8px 12px',
    marginTop: 4,
    background: 'white',
    border: '1px solid #1f9d8f',
    color: '#1f9d8f',
    borderRadius: 6,
    cursor: 'pointer',
    minWidth: 96,
  },
  primaryBtn: {
    width: '100%',
    padding: '10px',
    marginTop: 20,
    background: '#1f9d8f',
    color: 'white',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
  },
  err: { marginTop: 12, color: '#d33', fontSize: 13 },
  foot: { marginTop: 16, fontSize: 12, color: '#999', lineHeight: 1.5 },
};
